import React, {
    CSSProperties,
    DragEvent,
    MouseEvent as ReactMouseEvent,
    useRef,
    useState,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { MapImage } from '../../reducers/map/mapReducer';
import { shallowEqual, useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import MapElementShaper from '../Map/MapElementShaper';
import Client from '../../utils/network';

interface MapImageRenderProps {
    image: MapImage,
    onDragStart?: (e:DragEvent<HTMLImageElement>) => void,
    onClick?: (e: ReactMouseEvent) => void,
    className?: string,
    style?: CSSProperties,
    alt?: string,
    updateImage?: (image: MapImage) => void,
    onGrid?: boolean,
    showHidden?: boolean
}

const MapImageRender = ({
    image,
    onDragStart,
    onClick,
    style,
    className,
    alt,
    updateImage,
    onGrid,
    showHidden,
}: MapImageRenderProps) => {
    const id = image._id;
    const divRef = useRef<HTMLDivElement>(null);
    const [resize, setResize] = useState(false);

    const [currentResize, setCurrentResize] = useState<{ x: number, y: number}>();
    const [rotate, setRotate] = useState(false);

    const [currentRotate, setCurrentRotate] = useState<number>();

    const {
        currentLayer,
        selectedElement,
        zoom,
    } = useSelector((state: Rootstate) => ({
        currentLayer: state.map.mainLayer,
        selectedElement: state.map.selectedElement,
        zoom: state.zoom.zoom,
    }), shallowEqual);

    const isSelectedElement = selectedElement?._id === id && selectedElement.type === 'image';

    const startResize = (e: ReactMouseEvent<Element>) => {
        setResize(true);
        setCurrentResize({ x: image.width, y: image.height });
        const center = divRef.current?.getBoundingClientRect();

        const centerX = (center?.x ?? 0) + (center?.width ?? 0) / 2;
        const centerY = (center?.y ?? 0) + (center?.height ?? 0) / 2;
        const startDx = e.clientX - centerX;
        const startDy = e.clientY - centerY;

        const onMouseMove = (moveE: MouseEvent) => {
            const dx = moveE.clientX - centerX;
            const dy = moveE.clientY - centerY;
            const smallestD = Math.abs(dx/startDx) > Math.abs(dy/startDy)
                ? dx/startDx
                : dy/startDy;
            setCurrentResize({ x: image.width * smallestD, y: image.height * smallestD });
        };

        window.addEventListener('mousemove', onMouseMove);

        const onMouseUp = (upEvent: MouseEvent) => {
            const newImage = cloneDeep(image);

            const dx = upEvent.clientX - centerX;
            const dy = upEvent.clientY - centerY;
            const smallestD = Math.abs(dx/startDx) > Math.abs(dy/startDy)
                ? dx/startDx
                : dy/startDy;
            newImage.width *= smallestD;
            newImage.height *= smallestD;
            updateImage?.(newImage);
            setResize(false);
            setCurrentResize(undefined);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mouseup', onMouseUp);
    };

    const startRotate = (e: ReactMouseEvent<Element>) => {
        const center = divRef.current?.getBoundingClientRect();
        const dx = e.clientX - (center?.left as number + (center?.width as number / 2));
        const dy = (e.clientY - (center?.top as number + (center?.height as number / 2)));

        const startTheta = ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360;
        setCurrentRotate(0);
        setRotate(true);

        const onMouseMove = (e: MouseEvent) => {
            const center = divRef.current?.getBoundingClientRect();
            const dx = e.clientX - (center?.left as number + (center?.width as number / 2));
            const dy = (e.clientY - (center?.top as number + (center?.height as number / 2)));
            const theta = ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360;
            setCurrentRotate(((image.rotation + theta - startTheta) + 360) % 360);
        };

        window.addEventListener('mousemove', onMouseMove);

        const onMouseUp = (upEvent: MouseEvent) => {
            const center = divRef.current?.getBoundingClientRect();
            const dx = upEvent.clientX - (center?.left as number + (center?.width as number / 2));
            const dy = (upEvent.clientY - (center?.top as number + (center?.height as number / 2)));
            const endTheta = ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360;
            const newImage = cloneDeep(image);
            newImage.rotation = (newImage.rotation + endTheta - startTheta) % 360;
            setCurrentRotate(undefined);
            setRotate(false);
            updateImage?.(newImage);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mouseup', onMouseUp);
    };

    const newWidth = resize && isSelectedElement && currentResize
        ? `${currentResize.x}px`
        : style?.width;

    const newHeight = resize && isSelectedElement && currentResize
        ? `${currentResize.y}px`
        : style?.height;

    const newRotation = rotate && currentRotate
        ? `${((currentRotate) + 360) % 360}deg`
        : `${image.rotation ?? 0}deg`;

    const imageMatchesLayer = currentLayer === 'dm' && image.dm || currentLayer === 'public' && !image.dm;
    return imageMatchesLayer || showHidden ? (
        <>
            <div
                ref={divRef}
                className={`${className ?? ''}`}
                style={{
                    ...style,
                    zIndex: isSelectedElement ? '100': 'unset',
                    width: newWidth,
                    height: newHeight,
                    opacity: (imageMatchesLayer || isSelectedElement) ? 1 : 0.5,
                    transform: 'translate(-50%, -50%)'
                    + ` ${onGrid === false ? `scale(${zoom})` : ''}`
                    + ` rotate(${newRotation})`,
                }}
                draggable={imageMatchesLayer || isSelectedElement}
                onDragStart={imageMatchesLayer ? onDragStart : undefined}
                onClick={imageMatchesLayer ? onClick : undefined}
            >
                <img
                    className='w-full h-full'
                    alt={alt ?? 'image'}
                    src={`${Client.getBaseUrl()}/api/image/${image.image}`}
                />
                {selectedElement?._id === id && selectedElement.type === 'image' && (
                    <MapElementShaper
                        onResizeStart={startResize}
                        onRotateStart={startRotate}
                        dmLayer={image.dm}
                        rotation={parseInt((newRotation ?? '0deg').slice(0, -3))}
                        onLayerChange={(x) => {
                            const newImage = cloneDeep(image);
                            newImage.dm = x;
                            updateImage?.(newImage);

                        }}
                    />
                )}
            </div>

        </>

    ) : null;
};

export default MapImageRender;
